import { Slide, TableCell } from '@features/powerpoint';
import { contentSlideHeader, generateTable } from '../../../../utils';
import { formatters } from '@utils/formattersV2';
import { Subdimension } from '@features/reports/subdomains/visitation';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { TABLE_HEADER_OPTIONS } from '../sales-attribution/rsa-dimensions-table-slide';
import { MISSING_VALUE } from '@constants/stats';
import { MISSING_DOLLAR_VALUE } from '@utils/formatters';
import { UseCalculatedFieldsReturn } from '@features/campaigns';

type SummaryTableSlideProps = {
  slideMaster: string;
  subdimensions: Subdimension[];
  overallVisitMetrics: Lci3Report['visitMetrics'];
  slideHeader: string;
  calculatedFields: Pick<
    UseCalculatedFieldsReturn['data'],
    'costPerVisit' | 'costPerIncrementalVisit'
  >;
};

const visitAttributionColumnLabels = [
  {
    text: 'Dimension',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Total Impressions',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Unique Users Reached',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Total Projected Visits',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Visits',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Lift Confidence Level',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Optimization Index',
    options: TABLE_HEADER_OPTIONS,
  },
  { text: 'Cost Per Visit', options: TABLE_HEADER_OPTIONS },
  { text: 'Cost Per Incremental Visit', options: TABLE_HEADER_OPTIONS },
] as const satisfies TableCell[];

const formatMetrics = (
  visitMetrics: Lci3Report['visitMetrics'] | Subdimension['visitMetrics'],
  value: string,
  optimizationIndex?: number | null,
  calculatedFields?: SummaryTableSlideProps['calculatedFields']
): TableCell[] => {
  const impressions = formatters.impressions(visitMetrics.impressions, {
    full: true,
  });
  const uniqueUsers = formatters.users(visitMetrics.uniqueUsers, {
    full: true,
  });
  const projectedVisits = formatters.visits(visitMetrics.projectedVisits, {
    full: true,
  });
  const incrementalVisits = formatters.visits(visitMetrics.incrementalVisits, {
    full: true,
  });
  const incrementalLift = formatters.lift(visitMetrics.incrementalLift);
  const incrementalLiftConfidenceLevel = formatters.confidenceLevel(
    visitMetrics.confidenceLevel,
    visitMetrics.incrementalLift
  );
  const formattedOptimizationIndex =
    optimizationIndex === undefined
      ? MISSING_VALUE
      : formatters.number(optimizationIndex, { decimals: 2, full: true });

  const costPerVisit = calculatedFields
    ? formatters.currency(calculatedFields.costPerVisit)
    : MISSING_DOLLAR_VALUE;

  const costPerIncrementalVisit = calculatedFields
    ? formatters.currency(calculatedFields.costPerIncrementalVisit)
    : MISSING_DOLLAR_VALUE;

  return [
    { text: value },
    { text: impressions },
    { text: uniqueUsers },
    { text: projectedVisits },
    { text: incrementalVisits },
    { text: incrementalLift },
    { text: incrementalLiftConfidenceLevel },
    { text: formattedOptimizationIndex },
    { text: costPerVisit },
    {
      text: costPerIncrementalVisit,
    },
  ];
};

const subdimensionRows = (
  subdimensions: SummaryTableSlideProps['subdimensions'],
  overallVisitMetrics: SummaryTableSlideProps['overallVisitMetrics'],
  calculatedFields: SummaryTableSlideProps['calculatedFields']
): TableCell[][] => {
  const overallVisitMetricsRowData = formatMetrics(
    overallVisitMetrics,
    'Overall',
    undefined,
    calculatedFields
  );
  const subdimensionRowData = subdimensions.map(subdimension =>
    formatMetrics(
      subdimension.visitMetrics,
      subdimension.value,
      subdimension.visitMetrics.optimizationIndex
    )
  );

  return [overallVisitMetricsRowData, ...subdimensionRowData];
};

export const visitAttributionDimensionsSlide = (
  props: SummaryTableSlideProps
): Slide => {
  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTable(
        visitAttributionColumnLabels,
        subdimensionRows(
          props.subdimensions,
          props.overallVisitMetrics,
          props.calculatedFields
        )
      ),
    ],
    texts: [contentSlideHeader(props.slideHeader)],
  };
};
